import { useCallback, useEffect, useRef } from 'react';

import { AmplitudeExperimentIds, AmplitudeExperimentsVariantIds } from '../AmplitudeExperiments';
import { useAmplitudeFeatureFlag } from './useAmplitudeExperiments';

/**
 * Do not use this function directly! Use the corresponding experiment's hooks.
 *
 * Like useAmplitudeExperimentBase, but exposure is NOT fired on mount.
 * Use when the component mounts before the user can see the experiment
 * (e.g. below-the-fold content, lazy-loaded modals, scroll-triggered features).
 * Call expose() at the point where the user actually sees the treated UI.
 */
export const useAmplitudeExperimentBaseDeferred = (
    featureFlagKey: AmplitudeExperimentIds,
    featureFlagVariant: AmplitudeExperimentsVariantIds[typeof featureFlagKey]
) => {
    const { isShowingFeature, trackFeatureFlag } = useAmplitudeFeatureFlag(featureFlagKey, featureFlagVariant);
    // ponytail: ref guard prevents double-exposure if caller accidentally calls expose() more than once
    const hasExposed = useRef(false);
    const cleanupRef = useRef<(() => void) | undefined>(undefined);

    const expose = useCallback(() => {
        if (hasExposed.current) {
            return;
        }
        hasExposed.current = true;
        cleanupRef.current = trackFeatureFlag() ?? undefined;
    }, [trackFeatureFlag]);

    useEffect(() => {
        return () => {
            cleanupRef.current?.();
        };
    }, []);

    return { expose, isShowingFeature };
};
